import {
  MouseEvent as ReactMouseEvent,
  TouchEvent as ReactTouchEvent,
  useCallback,
  useEffect,
  useRef,
} from 'react';
import {twMerge} from '../../../common/twMerge';
import {PreloadedMedia} from '../../../domain/content/[seriesId]/viewer/[productId]/type/PreloadedMedia.interface';
import {TalkMediaStatus} from '../../../domain/content/[seriesId]/viewer/[productId]/type/TalkMediaStatus.enum';
import {formatTalkViewerMediaTime} from '../../../domain/content/[seriesId]/viewer/[productId]/util/formatTalkViewerMediaTime';
import {setMediaCurrentTime} from '../../../domain/content/[seriesId]/viewer/[productId]/util/setMediaCurrentTime';
import {Icon} from '../../atoms/Icon/Icon';
import {IconType} from '../../atoms/Icon/IconType.enum';

interface FullscreenContollerProps {
  // 해당 비디오를 직접 조작하기 위해 비디오 element를 props로 받음 ( currentTime 조절, 이벤트 추가 등 )
  videoElement: HTMLVideoElement;
  mediaStatus: TalkMediaStatus;
  changeVideoMediaStatus: (status: TalkMediaStatus) => void;
  isShowControls: boolean;
  isMobile: boolean;
  preloadedMedia?: PreloadedMedia;
  currentTime: number;
  videoDuration: number;
}

export default function FullscreenContoller({
  videoElement,
  mediaStatus,
  changeVideoMediaStatus,
  isShowControls,
  isMobile,
  currentTime,
  videoDuration,
}: FullscreenContollerProps) {
  const progressRef = useRef<HTMLDivElement>(null);
  const seekbarPosition = useRef(INITIAL_SEEKBAR_POSTION);
  const animationTimer = useRef(0);
  const videoStatusBeforeSeeking = useRef<TalkMediaStatus>(
    TalkMediaStatus.Play
  );

  // progressBar 애니메이션을 담당. videoElement에 직접 이벤트를 붙여서 타입 업데이트때마다 시크바를 이동시켜주는 방식
  useEffect(() => {
    function timeUpdateHandler(e: Event) {
      const targetVideo = e.target as HTMLVideoElement;
      function animationStep() {
        // 정지중에는 애니메이션 동작하지 않음
        if (!progressRef.current || targetVideo.paused) {
          return;
        }
        const progress = targetVideo.currentTime / targetVideo.duration;
        progressRef.current.style.width = `${progress * 100}%`;
        animationTimer.current = window.requestAnimationFrame(animationStep);
      }
      animationTimer.current = window.requestAnimationFrame(animationStep);
    }
    videoElement.addEventListener('timeupdate', timeUpdateHandler);

    return () => {
      videoElement.removeEventListener('timeupdate', timeUpdateHandler);
      window.cancelAnimationFrame(animationTimer.current);
    };
  }, [videoElement]);

  // 풀스크린 비디오는 재생 <-> 일시정지 토글만 가능하다.
  const onClickVideoBtn = useCallback(
    (e: ReactMouseEvent | ReactTouchEvent) => {
      e.stopPropagation();
      if (mediaStatus === TalkMediaStatus.Play) {
        changeVideoMediaStatus(TalkMediaStatus.Pause);
      } else {
        changeVideoMediaStatus(TalkMediaStatus.Play);
      }
    },
    [mediaStatus, changeVideoMediaStatus]
  );

  // 마우스클릭or터치 도중 시크바를 움직일 때 좌표를 저장하는 함수
  const onMoveSeekBar = useCallback(
    (e: TouchEvent | MouseEvent) => {
      const touchClientX = 'touches' in e ? e.touches[0].clientX : e.clientX;
      const seekbarWidth = seekbarPosition.current.width;
      const leftMarginValue = seekbarPosition.current.left;
      // 현재 이동중인 X좌표에서 seekbar left margin을 뺴 준 값을 seekbar 좌표로 사용.
      const movePosition = touchClientX - leftMarginValue;
      let releasePosition;
      if (movePosition < 0) {
        releasePosition = 0;
      } else if (movePosition > seekbarWidth) {
        releasePosition = seekbarWidth;
      } else {
        releasePosition = movePosition;
      }

      seekbarPosition.current.releasePosition = releasePosition;

      const newProgress = releasePosition / seekbarWidth;
      if (progressRef.current) {
        progressRef.current.style.width = `${newProgress * 100}%`;
      }

      setMediaCurrentTime(videoElement, videoDuration * newProgress);
    },
    [videoDuration, videoElement]
  );

  /**
   * React element는 event 타입을 SyntheticEvent(합성이벤트) 로 받게 되어있는데 SyntheticEvent에는 TouchEvent에 존재하는
   * changedTouches가 정의되어 있지 않아 any로 받음
   */
  const onReleaseSeekBar = useCallback(
    (e: any) => {
      e.stopPropagation();
      window.removeEventListener(getMoveEventType(isMobile), onMoveSeekBar);
      let releasePosition = seekbarPosition.current.releasePosition;

      // seekbar move가 일어나지 않았으면 터치가 일어난 좌표로 releasePosition을 잡아준다.
      if (releasePosition === undefined) {
        const clientX =
          'changedTouches' in e ? e.changedTouches[0].clientX : e.clientX;
        releasePosition = clientX - seekbarPosition.current.left;
      }
      const progress = Math.max(
        0,
        releasePosition / seekbarPosition.current.width
      );
      setMediaCurrentTime(videoElement, videoDuration * progress);
      seekbarPosition.current.releasePosition = undefined;
      if (progressRef.current) {
        progressRef.current.style.width = `${progress * 100}%`;
      }
      changeVideoMediaStatus(videoStatusBeforeSeeking.current);
    },
    [
      isMobile,
      onMoveSeekBar,
      videoElement,
      videoDuration,
      changeVideoMediaStatus,
    ]
  );

  const onPressSeekBar = useCallback(
    (e: ReactMouseEvent | ReactTouchEvent) => {
      e.stopPropagation();
      if (!progressRef.current) {
        return;
      }
      // pc에서는 영역밖으로 나가서 마우스를 떼면 해당 element에 부여된 mouseup은 작동하지 않는다. 그래서 window에 이벤트를 걸어준다.
      if (e.type === 'mousedown') {
        window.addEventListener('mouseup', onReleaseSeekBar, {
          once: true,
          capture: true,
        });
      }
      // @ts-ignore
      const seekbarWidth = e.currentTarget.offsetWidth;
      // @ts-ignore
      const seekbarLeft = e.currentTarget.offsetLeft;
      seekbarPosition.current.width = seekbarWidth;
      seekbarPosition.current.left = seekbarLeft;
      // 시크바 탐색 전 비디오 상태 저장. Play상태면 상태를 저장 후 비디오를 Pause상태로 바꿔놓는다.
      if (videoElement.paused) {
        videoStatusBeforeSeeking.current = TalkMediaStatus.Pause;
      } else {
        videoStatusBeforeSeeking.current = TalkMediaStatus.Play;
        changeVideoMediaStatus(TalkMediaStatus.Pause);
      }
      window.addEventListener(getMoveEventType(isMobile), onMoveSeekBar);
    },
    [
      onMoveSeekBar,
      isMobile,
      onReleaseSeekBar,
      videoElement,
      changeVideoMediaStatus,
    ]
  );

  return (
    <div
      onClick={e => e.stopPropagation()}
      className={twMerge(
        'absolute bottom-0 z-9999 h-180tvs w-full bg-static-mono-black pt-20tvs text-static-mono-white opacity-80 pb-safe',
        !isShowControls && 'pointer-events-none invisible'
      )}
    >
      <div
        className={twMerge(
          'mx-30tvs box-border flex h-1/5 items-center',
          'pr-safe pl-safe'
        )}
      >
        {/* currentTime은 변경시마다 정확히 update되어야 하므로 video ref에서 꺼내지 않고 props로 받아 사용 */}
        <span className={twMerge(TIME_STYLE, 'text-static-point-yellow')}>
          {formatTalkViewerMediaTime(currentTime)}
        </span>
        <div
          className="mx-12tvs flex h-[140%] flex-1 items-center"
          onTouchStart={isMobile ? onPressSeekBar : undefined}
          onTouchEnd={onReleaseSeekBar}
          // pc에서만 사용할 이벤트
          onMouseDown={isMobile ? undefined : onPressSeekBar}
        >
          {/* TODO: @Root-huh bg-[#424141] - 매칭되는 디자인 토큰 없음 */}
          <div className="relative h-7tvs flex-1 bg-[#424141]">
            <div
              className="absolute left-0 h-full bg-static-point-yellow after:absolute after:top-10tvs-minus after:right-13.5tvs-minus after:h-27tvs after:w-27tvs after:rounded-full after:bg-static-point-yellow"
              ref={progressRef}
            />
          </div>
        </div>
        <span className={twMerge(TIME_STYLE, 'text-static-mono-40')}>
          {formatTalkViewerMediaTime(videoDuration)}
        </span>
      </div>
      <div className="flex h-4/5 items-center justify-center">
        <Icon
          onClick={onClickVideoBtn}
          type={
            mediaStatus === TalkMediaStatus.Play
              ? IconType.BtnFullscreenVideoPause
              : IconType.BtnFullscreenVideoPlay
          }
          className="mb-20tvs w-84tvs"
        />
      </div>
    </div>
  );
}

function getMoveEventType(isMobile: boolean) {
  return isMobile ? 'touchmove' : 'mousemove';
}

const INITIAL_SEEKBAR_POSTION: {
  width: number;
  left: number;
  releasePosition?: number;
} = {
  width: 0,
  left: 0,
  // 시크바를 사용자가 움직이고 있을 때의 seekbar position 값
  releasePosition: undefined,
};

const TIME_STYLE = ['inline-block text-center min-w-68tvs text-24tvs'];
